import type { Permission } from '@munifocus/access';
import type { OperationsStatus } from '@munifocus/contracts-gov';
import { Icons, type LucideIcon } from '@munifocus/ui';

/** A primary navigation destination, shared by the desktop rail and the mobile tab bar. */
export interface NavItem {
  key: string;
  path: string;
  icon: LucideIcon;
  labelKey: string;
  /** Whether this item owns the current route. */
  isActive: (pathname: string) => boolean;
  /**
   * Which live counter badges this item, if any.
   *
   * A key rather than a number: this module is a static config with no access to
   * server state, and threading a count through it would make the whole nav
   * re-render on every message. The rail resolves the count itself.
   */
  badge?: 'messages' | 'inbox' | 'handovers';
  /**
   * Whether this item is worth offering at all, given where the account can
   * work. Absent means always.
   *
   * Distinct from permission gating inside the screen: Administration's own
   * tabs are permission-gated and "My access" is deliberately open to everyone,
   * because "why can't I see that file?" is the question a scoped system
   * creates. But an account attached to nowhere has no answer to explain, so
   * the rail should not send them to a screen with nothing on it.
   */
  isVisible?: (context: { scopeCount: number }) => boolean;
  /**
   * Any one of these permissions admits the destination. Absent means always.
   *
   * Separate from {@link isVisible} because it answers a different question:
   * `isVisible` asks whether there is anything *there*, this asks whether this
   * account may act. A contractor's employee who holds no appointment has both
   * a scope and a company, and was still being offered Capture, Map and the
   * docket — three screens whose entire content is files they cannot open.
   *
   * **Any, not all, and deliberately not the permission the screen uses.** An
   * officer holds `file:create` but *not* municipality-wide `file:read` — they
   * read the files they are assigned, through a file-scope role granted per
   * file. Gating Capture on `evidence:add` would therefore have hidden it from
   * every enforcement officer in the province, because that permission reaches
   * them the same way. `company_officer` is the one scope-level role holding
   * neither of these, which is exactly the case being excluded.
   */
  needsAny?: Permission[];
  /**
   * Hidden when the account holds any of these. For the public view of files:
   * offered only to someone without casework, since anyone who can open the
   * files themselves has no use for the public's-eye list beside them.
   */
  hiddenWhenAny?: Permission[];
  /**
   * A module a municipality turns on (through its plan, or a per-town switch).
   * The item is offered only once the server says it is on, so a town that has
   * not turned a module on sees the rail it always saw.
   */
  feature?: NavFeature;
  /**
   * For the contractor-operations screens: offered only when
   * `GET /operations/status` says so. Decided by this alone — the module is
   * switched on per municipality and off by default, and a contractor's
   * invoices are kept at the company while its dispatch happens at a town, so
   * the active scope's permissions cannot answer it. Hidden while unknown.
   */
  needsOperations?: (status: OperationsStatus) => boolean;
}

/** The switchable modules that can gate a rail item. */
export type NavFeature =
  'tickets' | 'costRecovery' | 'permits' | 'inspections' | 'analytics' | 'inspectionBooking';

/** A titled section of the desktop rail. */
export interface NavGroup {
  id: string;
  /** i18n key for the section heading. */
  labelKey: string;
  items: NavItem[];
}

/**
 * Officer Portal navigation, grouped by what the officer is *doing* — matching
 * how the Admin Portal's sidebar is organised.
 *
 * Two sections, not four. The flat list this replaced had grown to six unrelated
 * entries with no ordering logic; splitting six items across four headings would
 * trade one legibility problem for another, so the split is at the one boundary
 * that actually means something: work on files, versus the officer's own
 * workspace.
 *
 * The phone tab bar takes four of these, chosen by role, and lists the rest
 * under the same section headings in its More sheet (see `mobile-tabs.ts`).
 */
/**
 * Holding either is enough to be doing casework here.
 *
 * A clerk reads every file at the municipality; an officer creates them and
 * reads the ones they are assigned. Requiring both would exclude one of them,
 * and requiring `evidence:add` would exclude both — it reaches an officer
 * through their per-file role, not their appointment.
 */
export const CASEWORK: Permission[] = ['file:read', 'file:create'];

export const NAV_GROUPS: NavGroup[] = [
  {
    id: 'casework',
    labelKey: 'gov.nav.groups.casework',
    items: [
      {
        key: 'files',
        path: '/',
        icon: Icons.LayoutDashboard,
        labelKey: 'gov.nav.files',
        isActive: (p) => p === '/' || p.startsWith('/files'),
        needsAny: CASEWORK,
      },
      {
        // A limited-licence municipality's Files: every file, as the public
        // sees it. Takes the place of the casework screens it cannot open.
        key: 'publicFiles',
        path: '/public-files',
        icon: Icons.LayoutDashboard,
        labelKey: 'gov.nav.files',
        isActive: (p) => p.startsWith('/public-files'),
        needsAny: ['file:public_read'],
        hiddenWhenAny: CASEWORK,
      },
      {
        key: 'capture',
        path: '/capture',
        icon: Icons.Camera,
        labelKey: 'gov.nav.capture',
        isActive: (p) => p.startsWith('/capture'),
        needsAny: CASEWORK,
      },
      {
        key: 'map',
        path: '/map',
        icon: Icons.Map,
        labelKey: 'gov.nav.map',
        isActive: (p) => p.startsWith('/map'),
        needsAny: CASEWORK,
      },
      {
        // The supervisor's live team map. Only offered where the account holds
        // tracking:view, and empty unless the municipality turned tracking on.
        key: 'team',
        path: '/team',
        icon: Icons.Users,
        labelKey: 'gov.nav.team',
        isActive: (p) => p.startsWith('/team'),
        needsAny: ['tracking:view'],
      },
      {
        // The supervisor's safety board: alerts to answer and timed visits.
        key: 'safety',
        path: '/safety',
        icon: Icons.ShieldAlert,
        labelKey: 'gov.nav.safety',
        isActive: (p) => p.startsWith('/safety'),
        needsAny: ['safety:respond'],
      },
      {
        // Tickets and fines. Only where the plan grants `gov.tickets`.
        key: 'tickets',
        path: '/tickets',
        icon: Icons.Receipt,
        labelKey: 'gov.nav.tickets',
        isActive: (p) => p.startsWith('/tickets'),
        needsAny: ['ticket:read'],
        feature: 'tickets',
      },
      {
        // The municipality's cost recovery ledger. Only where the town has
        // turned cost recovery on, and only to an account that may read it.
        key: 'costRecovery',
        path: '/cost-recovery',
        icon: Icons.Banknote,
        labelKey: 'gov.nav.costRecovery',
        isActive: (p) => p.startsWith('/cost-recovery'),
        needsAny: ['cost:read'],
        feature: 'costRecovery',
      },
      {
        key: 'triage',
        path: '/triage',
        icon: Icons.ClipboardList,
        labelKey: 'gov.nav.triage',
        isActive: (p) => p.startsWith('/triage'),
        needsAny: CASEWORK,
      },
      {
        key: 'docket',
        path: '/docket',
        icon: Icons.Gavel,
        labelKey: 'gov.nav.docket',
        isActive: (p) => p.startsWith('/docket'),
        needsAny: CASEWORK,
      },
      {
        // Permit and licence applications, and the permits issued.
        key: 'permits',
        path: '/permits',
        icon: Icons.FileBadge,
        labelKey: 'gov.nav.permits',
        isActive: (p) => p.startsWith('/permits'),
        needsAny: ['permit:read'],
        feature: 'permits',
      },
      {
        // Checklist inspections: the calendar, and carrying one out.
        key: 'inspections',
        path: '/inspections',
        icon: Icons.ClipboardCheck,
        labelKey: 'gov.nav.inspections',
        isActive: (p) => p.startsWith('/inspections'),
        needsAny: ['inspection:read'],
        feature: 'inspections',
      },
    ],
  },
  {
    // Contractor operations. Every item is hidden unless the module is
    // switched on somewhere the account works — see `needsOperations`.
    id: 'operations',
    labelKey: 'gov.nav.groups.operations',
    items: [
      {
        key: 'dispatch',
        path: '/operations/dispatch',
        icon: Icons.UserCheck,
        labelKey: 'gov.nav.dispatch',
        isActive: (p) => p.startsWith('/operations/dispatch'),
        needsOperations: (s) => s.municipalities.some((m) => m.canDispatch),
      },
      {
        key: 'myDay',
        path: '/operations/my-day',
        icon: Icons.Route,
        labelKey: 'gov.nav.myDay',
        isActive: (p) => p.startsWith('/operations/my-day'),
        needsOperations: (s) => s.municipalities.some((m) => m.canReportTime),
      },
      {
        key: 'time',
        path: '/operations/time',
        icon: Icons.Clock,
        labelKey: 'gov.nav.time',
        isActive: (p) => p.startsWith('/operations/time'),
        needsOperations: (s) => s.municipalities.some((m) => m.canReportTime || m.canApproveTime),
      },
      {
        key: 'invoices',
        path: '/operations/invoices',
        icon: Icons.Banknote,
        labelKey: 'gov.nav.invoices',
        isActive: (p) => p.startsWith('/operations/invoices'),
        needsOperations: (s) =>
          s.companies.length > 0 || s.municipalities.some((m) => m.canReadInvoices),
      },
    ],
  },
  {
    id: 'workspace',
    labelKey: 'gov.nav.groups.workspace',
    items: [
      {
        key: 'inbox',
        path: '/inbox',
        icon: Icons.Inbox,
        labelKey: 'gov.nav.inbox',
        isActive: (p) => p.startsWith('/inbox'),
        badge: 'inbox',
      },
      {
        key: 'handovers',
        path: '/handovers',
        icon: Icons.ArrowRightLeft,
        labelKey: 'gov.nav.handovers',
        isActive: (p) => p.startsWith('/handovers'),
        badge: 'handovers',
        needsAny: CASEWORK,
      },
      {
        // The officer's re-inspection availability and booked appointments.
        // Offered only where a municipality in reach has booking on.
        key: 'availability',
        path: '/availability',
        icon: Icons.CalendarClock,
        labelKey: 'gov.nav.availability',
        isActive: (p) => p.startsWith('/availability'),
        needsAny: CASEWORK,
        feature: 'inspectionBooking',
      },
      {
        // The municipal administrator's one page: appointments, Form 4 name
        // approvals, council authorizations and active files by stage. Held by
        // the clerk seat — the Clerk and the Deputy or Assistant Clerks under
        // it — including at a municipality that does not subscribe.
        key: 'clerkDashboard',
        path: '/clerk',
        icon: Icons.BadgeCheck,
        labelKey: 'gov.nav.clerkDashboard',
        isActive: (p) => p.startsWith('/clerk'),
        needsAny: ['clerk_dashboard:read'],
      },
      {
        // Documents waiting on the caller's signature, and their own requests.
        key: 'signoffs',
        path: '/signoffs',
        icon: Icons.FileSignature,
        labelKey: 'gov.nav.signoffs',
        isActive: (p) => p.startsWith('/signoffs'),
        needsAny: CASEWORK,
      },
      {
        // The counter: calls, walk-ins and emails, and the callbacks they need.
        key: 'frontDesk',
        path: '/front-desk',
        icon: Icons.Phone,
        labelKey: 'gov.nav.frontDesk',
        isActive: (p) => p.startsWith('/front-desk'),
        needsAny: ['frontdesk:log'],
      },
      {
        key: 'callbacks',
        path: '/callbacks',
        icon: Icons.CalendarClock,
        labelKey: 'gov.nav.callbacks',
        isActive: (p) => p.startsWith('/callbacks'),
        needsAny: ['frontdesk:log'],
      },
      {
        key: 'messages',
        path: '/messages',
        icon: Icons.MessageSquare,
        labelKey: 'gov.nav.messages',
        isActive: (p) => p.startsWith('/messages'),
        badge: 'messages',
      },
      {
        // Requests between the municipality and its contractor, either way.
        key: 'requests',
        path: '/requests',
        icon: Icons.Send,
        labelKey: 'gov.nav.requests',
        isActive: (p) => p.startsWith('/requests'),
        needsAny: ['request:read'],
      },
      {
        // The council dashboard, and the contractor's comparison. Offered only
        // once `GET /analytics/availability` says analytics is on in reach.
        key: 'analytics',
        path: '/analytics',
        icon: Icons.BarChart3,
        labelKey: 'gov.nav.analytics',
        isActive: (p) => p.startsWith('/analytics'),
        needsAny: ['analytics:read', 'analytics:benchmark'],
        feature: 'analytics',
      },
      {
        key: 'administration',
        path: '/administration',
        icon: Icons.Landmark,
        labelKey: 'gov.nav.administration',
        isActive: (p) => p.startsWith('/administration'),
        isVisible: ({ scopeCount }) => scopeCount > 0,
      },
      {
        key: 'settings',
        path: '/settings',
        icon: Icons.SlidersHorizontal,
        labelKey: 'gov.nav.settings',
        isActive: (p) => p.startsWith('/settings'),
      },
    ],
  },
];

/**
 * The topbar title for a route. Ordered most-specific first, so `/files/new`
 * resolves before the `/files/:id` catch-all.
 *
 * Every signed-in route in `App.tsx` needs an entry: the topbar's title is the
 * page's one h1, and a route missing here rendered an empty bar on desktop and
 * no h1 at all (`/availability` did). `navigation-titles.spec.ts` holds the
 * line.
 */
const TITLE_ROUTES: { match: (pathname: string) => boolean; labelKey: string }[] = [
  { match: (p) => p === '/files/new', labelKey: 'gov.file.newTitle' },
  // Above the `/files/` catch-all below, which would otherwise title the batch
  // screen "File details" — it is not a file, and there is no id in the path.
  { match: (p) => p === '/files/import', labelKey: 'gov.bulkImport.title' },
  {
    match: (p) => /^\/files\/[^/]+\/tombstone\/?$/.test(p),
    labelKey: 'gov.extraction.tombstone.title',
  },
  {
    match: (p) => /^\/files\/[^/]+\/cost-recovery\/?$/.test(p),
    labelKey: 'gov.costRecovery.title',
  },
  { match: (p) => p.startsWith('/files/'), labelKey: 'gov.pageTitle.file' },
  { match: (p) => p === '/' || p.startsWith('/files'), labelKey: 'gov.dashboard.title' },
  { match: (p) => p.startsWith('/capture'), labelKey: 'gov.capture.title' },
  { match: (p) => p.startsWith('/map'), labelKey: 'gov.map.title' },
  { match: (p) => p.startsWith('/team/history'), labelKey: 'gov.team.history.title' },
  { match: (p) => p.startsWith('/team'), labelKey: 'gov.team.title' },
  { match: (p) => p.startsWith('/operations/dispatch'), labelKey: 'gov.ops.dispatch.title' },
  { match: (p) => p.startsWith('/operations/my-day'), labelKey: 'gov.ops.day.title' },
  { match: (p) => p.startsWith('/operations/time'), labelKey: 'gov.ops.time.title' },
  { match: (p) => p.startsWith('/operations/invoices'), labelKey: 'gov.ops.invoices.title' },
  { match: (p) => p.startsWith('/safety'), labelKey: 'gov.safety.board.title' },
  { match: (p) => p.startsWith('/messages'), labelKey: 'common.chat.title' },
  { match: (p) => p.startsWith('/inbox'), labelKey: 'gov.teamwork.inbox.title' },
  { match: (p) => p.startsWith('/handovers'), labelKey: 'gov.teamwork.handovers.title' },
  { match: (p) => p.startsWith('/clerk'), labelKey: 'gov.clerk.dashboard.title' },
  { match: (p) => p.startsWith('/signoffs'), labelKey: 'gov.signoff.queue.title' },
  {
    match: (p) => p.startsWith('/availability'),
    labelKey: 'gov.inspectionBooking.page.title',
  },
  { match: (p) => p.startsWith('/requests'), labelKey: 'gov.requests.title' },
  { match: (p) => p.startsWith('/public-files'), labelKey: 'gov.publicView.title' },
  { match: (p) => p.startsWith('/front-desk'), labelKey: 'gov.frontDesk.title' },
  { match: (p) => p.startsWith('/callbacks'), labelKey: 'gov.frontDesk.callbacks.title' },
  { match: (p) => p.startsWith('/triage'), labelKey: 'gov.triage.title' },
  { match: (p) => p.startsWith('/analytics'), labelKey: 'gov.analytics.title' },
  { match: (p) => p.startsWith('/cost-recovery'), labelKey: 'gov.costRecovery.ledger.title' },
  { match: (p) => p.startsWith('/docket'), labelKey: 'gov.docket.title' },
  { match: (p) => p.startsWith('/tickets'), labelKey: 'gov.tickets.title' },
  { match: (p) => p.startsWith('/permits'), labelKey: 'gov.permits.title' },
  { match: (p) => p.startsWith('/inspections'), labelKey: 'gov.inspections.title' },
  { match: (p) => p.startsWith('/administration'), labelKey: 'gov.admin.title' },
  { match: (p) => p.startsWith('/settings'), labelKey: 'gov.settings.title' },
];

/** Resolve the i18n key for a route's topbar title, or `null` if it has none. */
export function navTitleKey(pathname: string): string | null {
  return TITLE_ROUTES.find((r) => r.match(pathname))?.labelKey ?? null;
}

/** Whether a switchable module is on, off, or still being asked. */
export type FeatureState = 'on' | 'off' | 'pending';

/**
 * What the rail does with one item: draw it, leave it out, or hold its place
 * with a skeleton row while the module switch that decides it is in flight.
 */
export type NavItemState = 'show' | 'hide' | 'pending';

export interface NavItemContext {
  features: Record<NavFeature, FeatureState>;
  /**
   * The keys the rail showed the last time everything had resolved for this
   * scope, kept across reloads. While anything is still loading the rail draws
   * exactly that, so a cold load paints the rail it will settle on.
   */
  lastVisible: ReadonlySet<string> | null;
  pathname: string;
  /** Null without a ScopeProvider (isolated component tests). */
  scope: {
    isLoading: boolean;
    can: (permission: Permission) => boolean;
    scopeCount: number;
    operations?: OperationsStatus | null;
    operationsLoading?: boolean;
  } | null;
}

/** Whether anything about the account decides if this item is offered. */
const isGated = (item: NavItem) =>
  Boolean(
    item.feature || item.needsAny || item.hiddenWhenAny || item.isVisible || item.needsOperations,
  );

/**
 * Decide one rail item.
 *
 * The rail must be stable from its first paint. It used to show every
 * permission-gated item while the session loaded and hide module-gated ones
 * until their switch answered — so for a moment it listed Files twice, Team
 * and Safety, then dropped them, and Cost recovery arrived three rows late.
 * Now, while anything that decides an item is still loading, the item is, in
 * order: shown if it owns the current page; drawn as the rail last settled for
 * this scope; shown if nothing gates it; otherwise a placeholder row of the
 * same height (or left out, for the off-by-default operations screens).
 */
export function navItemState(item: NavItem, ctx: NavItemContext): NavItemState {
  const { scope } = ctx;
  if (item.feature && ctx.features[item.feature] === 'off') return 'hide';
  const pending =
    (!!scope && scope.isLoading && isGated(item)) ||
    (!!item.feature && ctx.features[item.feature] === 'pending') ||
    (!!item.needsOperations && !!scope?.operationsLoading);
  if (pending) {
    if (item.isActive(ctx.pathname)) return 'show';
    if (ctx.lastVisible) return ctx.lastVisible.has(item.key) ? 'show' : 'hide';
    return item.needsOperations ? 'hide' : 'pending';
  }
  if (item.needsOperations) {
    return scope?.operations && item.needsOperations(scope.operations) ? 'show' : 'hide';
  }
  if (!isGated(item)) return 'show';
  // No provider (isolated component tests): nothing is hidden.
  if (!scope) return 'show';
  if (item.needsAny && !item.needsAny.some((permission) => scope.can(permission))) return 'hide';
  if (item.hiddenWhenAny?.some((permission) => scope.can(permission))) return 'hide';
  if (item.isVisible && !item.isVisible({ scopeCount: scope.scopeCount })) return 'hide';
  return 'show';
}
